<template>
  <li class="row" @click="navigateToDetailsPage">
    <div class="title">
      <strong>{{ quote.Metadata.Name }}</strong
      ><br />
      <span class="metadata">{{ formattedDate }}</span
      ><br />
      <span v-if="komNumber">
        {{ i18n.t('/Dashboard/Table/Kom Number') }}: {{ komNumber }}<br />
      </span>
      <span data-qa="approval-row-quote-number">
        {{ i18n.t('/Dashboard/Table/Quote Id') }}: {{ quote.QuoteId }}
      </span>
    </div>
    <div class="customer">
      <span>{{ customerName }}</span
      ><br />
      <span>{{ customerCompanyName }}</span
      ><br />
      <span>{{ leaseCompanyName }}</span>
    </div>
    <div class="salesemployee">
      <span>{{ employeeName }}</span>
      <span>{{ dealerName }}</span>
      <span>{{ quoteEstablishmentId }}</span>
    </div>
    <div class="status">
      <span>{{ quote.Status }}</span>
    </div>
    <div class="actions" :class="{ 'actions--single': !canViewVcr }">
      <button
        data-qa="approval-row-button-remark"
        :title="i18n.t('/Dashboard/Actions/Info')"
        @click.stop="toggleRemarkModal(true, $event)"
      >
        <icon icon="info" :disabled="!hasRemark || null" />
      </button>

      <button
        v-if="canViewVcr"
        data-qa="approval-row-button-vcr"
        :title="i18n.t('/Dashboard/Actions/VCR')"
        @click.stop="goToVcr"
      >
        <icon icon="precalculative-result" />
      </button>
    </div>

    <!-- Remark Modal -->
    <modal
      v-if="showRemarkModal"
      :title="i18n.t('/Dashboard/RemarkModalTitle')"
      @close="toggleRemarkModal(false, $event)"
    >
      <p>{{ quote.QuoteDetails.Remark }}</p>
    </modal>
  </li>
</template>
<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import DateHelper from '@/helpers/dateHelper'
import { mapGetters } from 'vuex'

export default {
  name: 'ManagerApprovalRow',
  components: {
    Icon,
    Modal,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showRemarkModal: false,
    }
  },
  computed: {
    ...mapGetters({
      canViewVcr: 'user/canViewVcr',
    }),
    customerName() {
      const customer = this.quote.QuoteDetails?.Customer
      if (customer) {
        const initials = customer.CustomerInitials || ''
        let fullName = initials
        const middleName = customer.CustomerMiddleName || ''
        if (middleName != '') {
          fullName = fullName.concat(' ', middleName)
        }
        const familyName = customer.CustomerFamilyName || ''
        return fullName.concat(' ', familyName).trim()
      }
      return ''
    },
    customerCompanyName() {
      return this.quote.QuoteDetails?.CustomerCompany?.CustomerCompanyName || ''
    },
    employeeName() {
      return this.quote.QuoteDetails?.Employee?.EmployeeName || ''
    },
    dealerName() {
      return this.quote.QuoteDetails?.Dealer?.DealerName || ''
    },
    quoteEstablishmentId() {
      return this.quote.QuoteDetails?.Dealer?.EstablishmentID || ''
    },
    leaseCompanyName() {
      return this.quote.QuoteDetails?.LeaseCompany?.Name || ''
    },
    formattedDate() {
      try {
        return DateHelper.formatDate(new Date(this.quote.Metadata?.LastUpdatedOn))
      } catch (error) {
        console.warn('Error formatting date:', error)
        return ''
      }
    },
    hasRemark() {
      const remark = this.quote.QuoteDetails?.Remark
      return remark != null && remark != ''
    },
    komNumber() {
      // Handle both KomNumber and KOMNumber (backend inconsistency)
      const cars = this.quote.QuoteDetails?.Cars
      if (cars && cars.length > 0) {
        const car = cars[0]
        if (car?.KomNumber?.Number && car.KomNumber.Number > 0) {
          return car.KomNumber.Number
        } else if (car?.KOMNumber?.Number && car.KOMNumber.Number > 0) {
          return car.KOMNumber.Number
        }
      }
      return null
    },
  },
  methods: {
    navigateToDetailsPage() {
      // Calculate the current tab index for "Beoordelen"
      const currentTabIndex = this.getCurrentBeoordelenTabIndex()

      // Preserve existing query parameters and add tab info
      const query = {
        ...this.$route.query,
        returnUrl: this.$route.path,
        tab: currentTabIndex,
      }

      this.$router.push({
        name: 'configuration-page',
        params: {
          quoteId: this.quote.QuoteId,
          establishmentId: this.quoteEstablishmentId,
          cstType: this.$route.params.cstType,
        },
        query,
      })
    },
    getCurrentBeoordelenTabIndex() {
      // Calculate tab index based on the same logic as QuoteOverviewPage
      const isTest = this.$route.query?.hasOwnProperty('isTest')
      const dealerSettings = this.$store.getters['user/dealerSettings']

      let tabIndex = 0

      // Add Voorraad (New) if in test mode with new stock flow
      if (isTest && dealerSettings.newStockFlowEnabled) {
        tabIndex++
      }

      // Add Voorraad tab
      tabIndex++

      // Add Offertes tab
      tabIndex++

      // Beoordelen is the next tab
      return tabIndex
    },
    toggleRemarkModal(showModal, e) {
      e.stopPropagation()
      if (this.hasRemark) this.showRemarkModal = showModal
    },
    goToVcr() {
      const currentUrl = window.location.href.replace(window.location.origin, '')

      this.$router.push({
        name: 'vcr-page',
        params: {
          establishmentId: this.$route.params.establishmentId,
          cstType: this.$route.params.cstType,
          quoteId: this.quote.QuoteId,
        },
        query: {
          returnUrl: currentUrl,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.metadata {
  color: #a3b8c0;
}

.actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

// only the remark button remains when the VCR button is hidden
.actions--single {
  grid-template-columns: 1fr;
}

.actions svg {
  width: 3em;
  height: 3em;
  margin: 6px;
}

.title,
.customer,
.salesemployee {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.salesemployee,
.status {
  display: flex;
  flex-direction: column;
}

.row {
  cursor: pointer;
}

.row:hover {
  background-color: #fafbfc;
}
</style>
